/**
 * Waitlist Page (v0.10.0 - Issue #383)
 * 
 * Clean, professional waitlist page for logged-out users.
 * Features static footer and minimal design matching homepage aesthetic.
 */

import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Turnstile } from '@marsidev/react-turnstile';
import { invitesApi } from '../lib/api/invites';
import { useAuthModal } from '../contexts/AuthModalContext';
import type { ApiError } from '../lib/api';
import Button from '../components/Button';
import Input from '../components/Input';
import StaticFooter from '../components/StaticFooter';

export default function WaitlistPage() {
  const navigate = useNavigate();
  const { openLoginModal } = useAuthModal();
  const [searchParams] = useSearchParams();
  const source = searchParams.get('source') || 'homepage';
  const prefilledEmail = searchParams.get('email') || '';

  const [email, setEmail] = useState(prefilledEmail);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState(false);
  
  // Get Turnstile site key from environment (optional - graceful degradation)
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

  const joinMutation = useMutation({
    mutationFn: () => {
      if (!email) {
        throw new Error('Please provide an email address');
      }
      return invitesApi.joinWaitlist({
        email,
        source: source as 'homepage' | 'blocked_signup' | 'other',
        turnstile_token: turnstileToken || undefined,
      });
    },
    onSuccess: () => {
      setSubmitted(true);
      setError(null);
    },
    onError: (err: Error) => {
      // Check if error is ApiError type
      const apiError = err as unknown as ApiError;
      
      if (apiError.code === 'BOT_CHECK_FAILED') {
        setError('Security check failed. Please refresh the page and try again.');
        setTurnstileError(true);
        return;
      }
      
      setError(err.message || 'Failed to join waitlist');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email) {
      setError('Please provide an email address');
      return;
    }

    joinMutation.mutate();
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-oro-bg text-text-primary flex flex-col justify-between">
        {/* Header */}
        <header className="px-4 pt-6 pb-4">
          <div className="flex items-center justify-center">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center">
                <span className="text-black font-bold text-sm">O</span>
              </div>
              <span className="font-bold text-lg text-text-primary">ORO MOVE<sup>™</sup></span>
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className="flex flex-col items-center pt-16 pb-8 px-4">
          <div className="text-center space-y-6 max-w-md w-full">
            <div className="space-y-3">
              <h1 className="text-2xl font-bold text-text-primary">You're on the waitlist!</h1>
              <p className="text-text-secondary">
                Thanks for joining. We'll notify you when invites become available.
              </p>
            </div>

            <div className="rounded-xl border border-oro-border bg-oro-surface p-6">
              <p className="text-sm text-text-secondary text-center mb-4">
                We're currently in invite-only beta. We'll reach out as soon as spots open up.
              </p>
              <Button
                onClick={() => navigate('/')}
                variant="primary"
                className="w-full"
              >
                Go Home
              </Button>
            </div>
          </div>
        </main>

        {/* Static Footer */}
        <StaticFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-oro-bg text-text-primary flex flex-col justify-between">
      {/* Header */}
      <header className="px-4 pt-6 pb-4">
        <div className="flex items-center justify-center">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center">
              <span className="text-black font-bold text-sm">O</span>
            </div>
            <span className="font-bold text-lg text-text-primary">ORO MOVE<sup>™</sup></span>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex flex-col items-center pt-4 pb-8 px-4">
        <div className="text-center space-y-4 max-w-md w-full">
          <h1 className="text-2xl font-bold text-text-primary">Join the Waitlist</h1>
          
          {/* Hero Card with Runner Image */}
          <div className="mb-6">
            <div className="relative rounded-xl overflow-hidden bg-oro-surface border border-oro-border mx-auto w-[80%] sm:w-full">
              <div 
                className="h-[416px] sm:h-[520px] bg-cover bg-center"
                style={{
                  backgroundImage: "url('/images/rahadiansyah-1PENRGjYcOY-unsplash.jpg')"
                }}
              >
              </div>
              <div className="absolute bottom-2 right-2 text-xs text-white/70 bg-black/30 px-2 py-1 rounded">
                Photo by <a href="https://unsplash.com/@rahadiansyah" className="underline">Rahadiansyah</a> on <a href="https://unsplash.com/photos/man-in-yellow-t-shirt-and-gray-shorts-sitting-on-gray-concrete-road-during-daytime-1PENRGjYcOY" className="underline">Unsplash</a>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-text-secondary">
              ORO MOVE<sup>™</sup> is invite-only. Join our waitlist to get early access.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                className="w-full"
                required
              />
            </div>

            {error && (
              <div className="rounded-md border border-red-500/40 bg-red-500/10 p-4">
                <p className="text-sm text-red-100">{error}</p>
              </div>
            )}

            {turnstileSiteKey && (
              <div className="space-y-3">
                <div className="flex justify-center">
                  <Turnstile
                    siteKey={turnstileSiteKey}
                    onSuccess={(token) => {
                      setTurnstileToken(token);
                      setTurnstileError(false);
                    }}
                    onError={() => {
                      setTurnstileError(true);
                      setTurnstileToken(null);
                    }}
                    onExpire={() => {
                      setTurnstileToken(null);
                    }}
                    options={{
                      theme: 'dark',
                      size: 'normal',
                    }}
                  />
                </div>
                {turnstileError && (
                  <div className="rounded-md border border-oro-border bg-oro-surface-alt p-3">
                    <p className="text-xs text-text-secondary text-center">
                      Security verification failed. Please refresh the page and try again.
                    </p>
                  </div>
                )}
              </div>
            )}

            <Button
              type="submit"
              variant="primary"
              disabled={joinMutation.isPending || !email}
              loading={joinMutation.isPending}
              className="w-full py-3"
            >
              Join Waitlist
            </Button>

            <p className="text-center text-sm text-text-secondary">
              Already have an invite?{' '}
              <button 
                onClick={() => openLoginModal()} 
                className="text-accent hover:text-accent-light transition-colors font-medium"
              >
                Sign In
              </button>
            </p>
          </form>
        </div>
      </main>

      {/* Static Footer */}
      <StaticFooter />
    </div>
  );
}
